import React, { useState, useCallback } from 'react';
import { Button, Card, CardBody, Col, Table } from 'reactstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { fetchAllCountryCurrency } from '../../../../store/apis/expenseService';
import { useProcessExpenseClaimCtx } from '../context/ProcessExpenseClaimProvider';
import { useExpenseClaimUtilCtx } from '../../context/ExpenseClaimUtilProvider';
import {
  ModalWrapper,
  ModalTable,
  ModalMultiWrapper,
  FilterDropdown,
  TableSortDisplayArrow,
  TableFilterBubbles,
  SpinnerLoading,
} from '../../../../components/Common';
import {
  CancelExpenseClaimModal,
  UploadAttachmentsModal,
  ViewAttachmentsModal,
  PrintModal,
  ProcessClaimRow,
  SaveProcessClaimModal,
  AdvanceRecoveryModal,
} from '../../../../components/ExpenseClaims';
import {
  processClaimSortConfig,
  firstHeaderColumn,
  secondHeaderColumn,
  thirdHeaderColumn,
} from './constants';
import { useConfirmationModal } from '../../../../components/hooks';
import { DateUtil, NumberUtil } from '../../../../utils';

import './ProcessExpenseClaims.scss';

const ACCEPT_FOR_REVIEW_TOAST_ID = 'AcceptForReviewToastError';
const ROLL_BACK_TRANSACTION_TOAST_ID = 'RollBackTransactionToastError';
const CANCEL_EXPENSE_CLAIM_TOAST_ID = 'CancelExpenseClaimToastError';
const SAVE_EXPENSE_CLAIM_TOAST_ID = 'SaveExpenseClaimToastError';

function ProcessExpenseClaim() {
  document.title = 'Expense Claims | Daikin Envision';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    formMethods: methods,
    formFieldArray,
    defaultCurrCode,
    expenseClaimHeaders,
    isModalOpen,
    modalConfig,
    resetExpenseClaimAll,
    viewAttachmentsModalOpen,
    viewAttachmentSrNo,
    setViewAttachmentsModalOpen,
    applyFilter,
    handleManualSort,
    resetFilters,
    checkColumnIfSorted,
    columnName,
    setColumnName,
    pageRequest,
    setPageRequest,
    getColumnData,
    generateChangeLogForExpenseClaims,
    runValidationsAndToastErrors,
    clearSingleFilterValue,
    expenseCatMapping,
    expenseTypeMapping,
    validateLastRow,
    isLoadingAll,
    handleAutoSort,
    setModalConfig,
    setIsModalOpen,
  } = useProcessExpenseClaimCtx();
  const {
    saveProcessExpenseClaimMutation,
    acceptProcessExpenseClaimMutation,
    submitProcessExpenseClaimMutation,
    rejectProcessExpenseClaimMutation,
  } = useExpenseClaimUtilCtx();
  const [searchText, setSearchText] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [multiModalOpen, setMultiModalOpen] = useState(false);
  const [multiModelName, setMultiModelName] = useState('');
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [uploadAttachmentsModalOpen, setUploadAttachmentsModalOpen] =
    useState(false);
  const { open, ConfirmationModal } = useConfirmationModal();
  const [advanceRecoveryModalOpen, setAdvanceRecoveryModalOpen] =
    useState(false);

  const { watch } = methods;
  const { fields, append, remove } = formFieldArray;
  const { data: currencyLists } = useQuery({
    queryKey: ['currencyLists', searchText],
    queryFn: () =>
      fetchAllCountryCurrency({
        prefixText: searchText,
        type: 'HR_CURRENCY',
        param1: '',
        param2: '',
      }),
  });

  const { data: accdList } = useQuery({
    queryKey: ['accdList', searchText],
    queryFn: () =>
      fetchAllCountryCurrency({
        prefixText: '',
        type: 'ACCD',
        param1: '',
        param2: '',
      }),
  });

  const generatePayload = values => {
    const expenseDetails = values.claims.map((row, idx) => ({
      ...row,
      srNo: row?.srNo || idx + 1,
    }));
    return expenseDetails;
  };

  const { errors: formErrors } = methods.formState;

  const renderTableBody = () =>
    fields.map((item, index) => (
      <ProcessClaimRow
        key={item.id}
        index={index}
        item={item}
        remove={remove}
        currencyLists={currencyLists}
        defaultCurrCode={defaultCurrCode}
        setSearchText={setSearchText}
        methods={methods}
        watch={watch}
        append={append}
        totalRows={fields.length}
        accdList={accdList}
        errorsExist={formErrors?.claims?.[index] && true}
      />
    ));

  const handleRejectProcessExpenseClaim = async payload => {
    // Cancel the expense claim
    const response = await rejectProcessExpenseClaimMutation.mutateAsync({
      claimNo: expenseClaimHeaders?.claimNo || '',
      reason: payload.cancelReason,
    });
    if (response?.success === true) {
      toast.success(
        `Expense Claim (${expenseClaimHeaders?.claimNo}) cancelled successfully.`,
        {
          className: 'toast-success',
        },
      );
      setMultiModalOpen(false);
      navigate('/expense/claim', { replace: true });
      resetExpenseClaimAll();
    } else {
      toast.error(
        response?.errorData ||
          'Failed to cancel expense claim. Please try again.',
        { className: 'toast-error', toastId: CANCEL_EXPENSE_CLAIM_TOAST_ID },
      );
    }
  };

  const handleSubmitForApprovalCall = async () => {
    try {
      const response = await submitProcessExpenseClaimMutation.mutateAsync({
        claimNo: expenseClaimHeaders?.claimNo || '',
        employeeId: expenseClaimHeaders?.employeeId || '',
      });
      if (response?.flag && response?.flag === 'True') {
        toast.success(
          `Expense Claim No #${expenseClaimHeaders?.claimNo} submitted for approval successfully!`,
          {
            className: 'toast-success',
          },
        );
        queryClient.invalidateQueries(['expenseClaimDetailsHeader']);
        queryClient.invalidateQueries(['expenseClaimDetails']);
        resetFilters();
        navigate('/hr/expense/process-search');
      }
      if (response?.flag === false || response?.flag === 'False') {
        if (response?.errorData && response?.errorData?.length > 0) {
          setModalConfig({
            title:
              'Expense Claim submission failed due to error specified below:',
            data: response?.errorData,
            dataConfig: null,
          });
          setIsModalOpen(true);
          return;
        }
        toast.error(
          <div>
            <div>{window.location.origin} says</div>
            <div>{response?.message}</div>
          </div>,
          {
            className: 'toast-error',
          },
        );
      }
    } catch (error) {
      const errorMessage =
        error?.error || error?.message || 'Roll back transaction failed';
      toast.error(
        errorMessage ||
          'The ROLLBACK TRANSACTION request has no corresponding BEGIN TRANSACTION. Mismatched in Claim Amount & GL Posting',
        {
          className: 'toast-error',
          toastId: ROLL_BACK_TRANSACTION_TOAST_ID,
        },
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      const submitChangeLogString = await generateChangeLogForExpenseClaims();
      if (submitChangeLogString) {
        setMultiModelName('SaveAndSubmitProcessClaimModal');
        setMultiModalOpen(true);
        return;
      }

      await handleSubmitForApprovalCall();
    } catch (error) {
      const errorMessage =
        error.response?.message || // axios/fetch with backend message
        error?.error || // custom field
        error?.message || // generic message
        'Failed to submit expense claim for approval.';

      toast.error(errorMessage, {
        className: 'toast-error',
        // remove toastId unless you need dedupe
      });
    }
  };

  const handleSaveExpenseClaim = async (values, payload, type = 'save') => {
    const expenseDetails = generatePayload(values);
    const finalPayload = {
      claimNo: expenseClaimHeaders?.claimNo || '',
      ...payload,
      hrExpenseDetails: expenseDetails,
    };
    try {
      if (type === 'submit') {
        setSubmitLoading(true);
      }
      const response =
        await saveProcessExpenseClaimMutation.mutateAsync(finalPayload);
      if (response?.success && response?.success === true) {
        if (type === 'save') {
          toast.success(
            `Process Claim details saved successfully with Claim No ${response?.claimNo}.`,
            {
              className: 'toast-success',
            },
          );
          queryClient.invalidateQueries(['expenseClaimDetailsHeader']);
          queryClient.invalidateQueries(['expenseClaimDetails']);
          resetFilters();
          setMultiModalOpen(false);
        }
        if (type === 'submit') {
          await handleSubmitForApprovalCall();
        }
      } else {
        toast.error(
          response?.message ||
            'Failed to save expense claim. Please try again.',
          {
            className: 'toast-error',
            toastId: SAVE_EXPENSE_CLAIM_TOAST_ID,
          },
        );
      }
    } catch (error) {
      toast.error(
        error?.message || 'An error occurred while saving the claim.',
        {
          className: 'toast-error',
          toastId: SAVE_EXPENSE_CLAIM_TOAST_ID,
        },
      );
    }
  };

  const handleSaveExpenseClaimWrapper = async (payload, type = 'save') => {
    const onSubmit = values => handleSaveExpenseClaim(values, payload, type);
    methods.handleSubmit(onSubmit)();
  };

  const handleAcceptForReview = async () => {
    const payload = {
      claimNo: expenseClaimHeaders?.claimNo || '',
      employeeId: expenseClaimHeaders?.employeeId || '',
    };
    try {
      const response =
        await acceptProcessExpenseClaimMutation.mutateAsync(payload);
      if (response?.success && response?.success === true) {
        toast.success(
          `Expense Claim No #${expenseClaimHeaders?.claimNo} status changed successfully!`,
          {
            className: 'toast-success',
          },
        );
        queryClient.invalidateQueries(['expenseClaimDetailsHeader']);
        queryClient.invalidateQueries(['expenseClaimDetails']);
        resetFilters();
      }
    } catch (error) {
      toast.error(
        error?.message || 'Failed to accept expense claim for review.',
        {
          className: 'toast-error',
          toastId: ACCEPT_FOR_REVIEW_TOAST_ID,
        },
      );
    }
  };

  const onSubmitClick = useCallback(async () => {
    const hasErrors = await runValidationsAndToastErrors();
    if (hasErrors) {
      return;
    }
    methods.handleSubmit(handleSubmitForApproval)();
  }, [methods, handleSubmitForApproval]);

  if (isLoadingAll) {
    return <SpinnerLoading spinnerSize="2rem" height="100vh" />;
  }

  return (
    <FormProvider {...methods}>
      <div className="page-content process-claim-page">
        <Card className="eec-card">
          <CardBody className="p-0">
            {/* Header */}
            <div className="eec-header">
              {/* Expense Claim Header */}
              <div className="eec-header-title px-2 d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="p-0 btn btn-link trd-back d-flex align-items-center"
                    onClick={() =>
                      navigate('/hr/expense/process-search', { replace: true })
                    }
                  >
                    <Icon path={mdiChevronLeft} size={1.1} />
                    <span className="back fs-5">Back</span>
                  </button>
                  <div
                    style={{
                      borderLeft: '1px solid lightgrey',
                      height: '30px',
                    }}
                  />
                  <span className="fw-semibold fs-5">Expense Claims</span>
                </div>
              </div>
              {/* Claim Card Info */}
              <div className="d-flex gap-2 px-2 pt-2 pb-2 flex-row align-items-center justify-content-between">
                <Col className="d-flex flex-column gap-2">
                  <div className="claim-info-card d-flex flex-column gap-3">
                    {firstHeaderColumn(expenseClaimHeaders)?.map(item => (
                      <div className="d-flex flex-row gap-3">
                        <div className="claim-icon">
                          <img
                            src={item.icon}
                            alt="Claim Number Icon"
                            className="claim-icon-img"
                          />
                        </div>
                        <div className="d-flex flex-column gap-1">
                          <div className="claim-title">{item?.title}</div>
                          <div>{item?.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col className="d-flex flex-column gap-2">
                  <div className="claim-info-card d-flex flex-column gap-3">
                    {secondHeaderColumn(expenseClaimHeaders)?.map(item => (
                      <div className="d-flex flex-row gap-3">
                        <div className="claim-icon">
                          <img
                            src={item.icon}
                            alt="Claim Number Icon"
                            className="claim-icon-img"
                          />
                        </div>
                        <div className="d-flex flex-column gap-1">
                          <div className="claim-title">{item?.title}</div>
                          <div>{item?.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col className="d-flex flex-column gap-2">
                  <div className="claim-info-card d-flex flex-column gap-3">
                    {thirdHeaderColumn(expenseClaimHeaders)?.map(item => (
                      <div className="d-flex flex-row gap-3">
                        <div className="claim-icon">
                          <img
                            src={item.icon}
                            alt="Claim Number Icon"
                            className="claim-icon-img"
                          />
                        </div>
                        <div className="d-flex flex-column gap-1">
                          <div className="claim-title">{item?.title}</div>
                          <div>{item?.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
              </div>
            </div>
            <div className="my-2 px-2">
              <TableFilterBubbles
                allFilters={pageRequest?.filters || []}
                clearSingleFilterValue={clearSingleFilterValue}
                clearAllFilters={resetFilters}
                mapping={{
                  expCategory: expenseCatMapping || {},
                  expType: expenseTypeMapping || {},
                }}
                render={{
                  expDate: date => DateUtil.isoToDdMmYyyySlice(date),
                }}
              />
            </div>
            {/* Body + Table Form */}
            <div className="table-responsive hr-eec-table-container">
              <Table className="">
                <thead>
                  <tr>
                    {processClaimSortConfig.map(column => (
                      <th key={column?.key}>
                        <div className="d-flex flex-row align-items-center justify-content-start gap-1">
                          <div
                            className="p-0 cursor-pointer"
                            onClick={() => handleAutoSort(column?.key)}
                          >
                            {column?.label}
                          </div>
                          <TableSortDisplayArrow
                            direction={checkColumnIfSorted(column?.key)}
                          />
                          <FilterDropdown
                            handleSetColumnName={() => {
                              setColumnName(column?.key);
                            }}
                            setColumnName={setColumnName}
                            columnData={getColumnData(column?.key)}
                            applyFilter={applyFilter}
                            columnName={columnName}
                            pageRequest={pageRequest}
                            setPageRequest={setPageRequest}
                            handleManualSort={handleManualSort}
                            mapping={{
                              expCategory: expenseCatMapping || {},
                              expType: expenseTypeMapping || {},
                            }}
                            render={{
                              expDate: date =>
                                DateUtil.isoToDdMmYyyySlice(date),
                            }}
                          />
                        </div>
                      </th>
                    ))}
                    <th className="fw-semibold py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="eec-table-body">{renderTableBody()}</tbody>
              </Table>
            </div>
            {/* Footer */}
            <div className="eec-footer d-flex align-items-center justify-content-end position-relative p-3">
              <div className="d-flex flex-row align-items-center position-absolute start-50 translate-middle-x">
                <div className="eec-footer-btns-center w-100">
                  <Button
                    type="button"
                    className="custom-danger-btn"
                    onClick={() => {
                      open({
                        title: 'Reject Expense Claim',
                        message:
                          'Are you sure you want to reject expense claim?',
                        onConfirm: () => {
                          setMultiModelName('CancelExpenseClaimModal');
                          setMultiModalOpen(true);
                        },
                        showCancelButton: true,
                      });
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    type="button"
                    className="custom-light-btn"
                    onClick={() => {
                      setUploadAttachmentsModalOpen(true);
                    }}
                    disabled={!expenseClaimHeaders?.claimNo}
                  >
                    Attachment
                  </Button>
                  <Button
                    type="button"
                    className="custom-light-btn text-nowrap"
                    onClick={handleAcceptForReview}
                  >
                    Accept for Review
                  </Button>
                  <Button
                    type="button"
                    className="custom-secondary-btn"
                    onClick={async () => {
                      // Check if last row  is exp cat , exp type or code being key in
                      const lastRowIndex =
                        methods.getValues('claims').length - 1;
                      const lastRow = methods.getValues('claims')[lastRowIndex];
                      if (
                        lastRow?.expCategory ||
                        lastRow?.expType ||
                        lastRow?.acctCode
                      ) {
                        // Trigger last row validation
                        const testRes = await validateLastRow(false, false);
                        if (!testRes?.isValid) {
                          const errorMessages = Object.values(testRes?.errors)
                            .map(err => err)
                            .join(', ');
                          toast.error(errorMessages, {
                            className: 'toast-error',
                          });
                          return;
                        }
                      }
                      const hasErrors =
                        await runValidationsAndToastErrors(true);
                      if (!hasErrors) {
                        setMultiModelName('SaveProcessClaimModal');
                        setMultiModalOpen(true);
                      }
                    }}
                    style={{
                      cursor: methods.formState?.isValid
                        ? 'pointer'
                        : 'not-allowed',
                      opacity: methods.formState?.isValid ? 1 : 0.5,
                    }}
                  >
                    Save
                  </Button>

                  <Button
                    type="button"
                    className="custom-secondary-btn text-nowrap"
                    onClick={onSubmitClick}
                    disabled={submitProcessExpenseClaimMutation?.isPending}
                    style={{
                      cursor: methods.formState?.isValid
                        ? 'pointer'
                        : 'not-allowed',
                      opacity: methods.formState?.isValid ? 1 : 0.5,
                    }}
                  >
                    {submitProcessExpenseClaimMutation?.isPending
                      ? 'Submitting...'
                      : 'Submit for Approval'}
                  </Button>
                </div>
              </div>
              <div className="eec-summary-top me-1">
                <div
                  className="eec-summary-title me-1"
                  title="Shows the total amount of the expense claim."
                >
                  {`Expense Total (${expenseClaimHeaders?.currency || 'Currency'}) :`}
                </div>
                <div className="text-dark fw-bol expense-total-container">
                  {NumberUtil.truncateNumber(
                    expenseClaimHeaders?.claimAmt || 0,
                    2,
                  ).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
      <ModalWrapper
        isModalOpen={isModalOpen}
        toggleModal={() => setIsModalOpen(!isModalOpen)}
        title={modalConfig.title}
        onButtonClick={() => setIsModalOpen(false)}
      >
        <ModalTable
          data={modalConfig.data}
          dataConfig={modalConfig.dataConfig}
        />
      </ModalWrapper>
      <ModalMultiWrapper
        modals={{
          SaveProcessClaimModal: {
            component: SaveProcessClaimModal,
            props: {
              handleOnSubmit: handleSaveExpenseClaimWrapper,
              isLoading: saveProcessExpenseClaimMutation.isPending,
              getChangeLog: generateChangeLogForExpenseClaims,
              type: 'save',
            },
          },
          SaveAndSubmitProcessClaimModal: {
            component: SaveProcessClaimModal,
            props: {
              handleOnSubmit: handleSaveExpenseClaimWrapper,
              isLoading: submitLoading,
              getChangeLog: generateChangeLogForExpenseClaims,
              type: 'submit',
            },
          },
          CancelExpenseClaimModal: {
            component: CancelExpenseClaimModal,
            props: {
              handleOnSubmit: handleRejectProcessExpenseClaim,
              isLoading: rejectProcessExpenseClaimMutation.isPending,
            },
          },
        }}
        isOpen={multiModalOpen}
        toggle={() => setMultiModalOpen(false)}
        selectedModal={multiModelName}
      />
      <PrintModal
        isOpen={isPrintModalOpen}
        toggle={() => setIsPrintModalOpen(!isPrintModalOpen)}
        claimNo={expenseClaimHeaders?.claimNo}
      />
      <UploadAttachmentsModal
        isOpen={uploadAttachmentsModalOpen}
        toggle={() =>
          setUploadAttachmentsModalOpen(!uploadAttachmentsModalOpen)
        }
        claimNo={expenseClaimHeaders?.claimNo || ''}
        finCoCd={expenseClaimHeaders?.finCoCd || ''}
      />
      <ViewAttachmentsModal
        isOpen={viewAttachmentsModalOpen}
        toggle={() => setViewAttachmentsModalOpen(false)}
        claimNo={expenseClaimHeaders?.claimNo}
        srNo={viewAttachmentSrNo}
        finCoCd={expenseClaimHeaders?.finCoCd || ''}
      />
      <ConfirmationModal />
      <AdvanceRecoveryModal
        isOpen={advanceRecoveryModalOpen}
        toggle={() => setAdvanceRecoveryModalOpen(false)}
        claimNo={expenseClaimHeaders?.claimNo || ''}
        employeeId={expenseClaimHeaders?.employeeId || ''}
      />
    </FormProvider>
  );
}

export default ProcessExpenseClaim;
